import api from "./axios";

/**
 * Bitrix deal type from search endpoint
 */
export type BitrixDeal = {
  id: number;
  title: string;
  category_id: number;
  stage_id: string;
  stage_name: string;
  budget: number | null;
  budget_note: string | null;
};

/**
 * Account with associated Bitrix deal info
 */
export type AccountWithDeal = {
  client_id: string;
  display_name: string;
  source_type: "allegro" | "google_ads";
  bitrix_deal: BitrixDeal | null;
  is_manual_assignment: boolean;
};

/**
 * Search Bitrix deals by title
 * @param query - Search query (min 2 characters)
 * @returns Array of matching Bitrix deals
 */
export async function searchBitrixDeals(query: string): Promise<BitrixDeal[]> {
  const response = await api.get<BitrixDeal[]>("/bitrix/deals/search", {
    params: { q: query },
  });
  return response.data;
}

/**
 * Get all accounts of a source type with their Bitrix deal info
 * @param sourceType - "allegro" or "google_ads"
 * @returns Array of accounts with deal information
 */
export async function getAccountDeals(
  sourceType: "allegro" | "google_ads"
): Promise<AccountWithDeal[]> {
  const response = await api.get<AccountWithDeal[]>(
    `/bitrix/accounts/${sourceType}/deals`
  );
  return response.data;
}

/**
 * Assign or unassign a Bitrix deal to an account
 * @param sourceType - "allegro" or "google_ads"
 * @param clientId - The client/customer ID
 * @param dealId - Bitrix deal ID to assign, or null to unassign
 */
export async function assignDealToAccount(
  sourceType: string,
  clientId: string,
  dealId: number | null
): Promise<void> {
  await api.put(`/bitrix/mappings/${sourceType}/${clientId}`, {
    bitrix_deal_id: dealId,
  });
}

/**
 * Remove deal assignment and reset manual flag (allows auto-match again)
 * @param sourceType - "allegro" or "google_ads"
 * @param clientId - The client/customer ID
 */
export async function removeDealAssignment(
  sourceType: string,
  clientId: string
): Promise<void> {
  await api.delete(`/bitrix/mappings/${sourceType}/${clientId}`);
}

/**
 * Auto-match response type
 */
export type AutoMatchResult = {
  source_type: string;
  client_id: string;
  bitrix_deal: BitrixDeal | null;
  is_manual_assignment: boolean;
  matched: boolean;
};

/**
 * Trigger auto-matching for a specific account
 * @param sourceType - "allegro" or "google_ads"
 * @param clientId - The client/customer ID
 * @returns Auto-match result with deal info if found
 */
export async function autoMatchAccount(
  sourceType: string,
  clientId: string
): Promise<AutoMatchResult> {
  const response = await api.post<AutoMatchResult>(
    `/bitrix/mappings/${sourceType}/${clientId}/auto-match`
  );
  return response.data;
}

/**
 * Batch auto-match result type
 */
export type BatchAutoMatchResult = {
  total: number;
  matched: number;
  skipped_manual: number;
  no_match: number;
  errors: number;
  details: Array<{
    source_type: string;
    client_id: string;
    status: "matched" | "skipped_manual" | "no_match" | "error";
    deal_id?: number;
    deal_title?: string;
    error?: string;
  }>;
};

/**
 * Batch auto-match all accounts from all active drafts
 * @returns Summary of matched/skipped/failed accounts
 */
export async function batchAutoMatchAllDrafts(): Promise<BatchAutoMatchResult> {
  const response = await api.post<BatchAutoMatchResult>("/bitrix/batch-auto-match");
  return response.data;
}
